// Checkout service for API calls
import { CheckoutData } from '../types';
import { fetchWithErrorHandling, guestPrefix } from './apiUtils';

export type CheckoutDiagnosticData = Record<string, unknown>;

export class CheckoutService {
  static async applyShippingRate(
    locationId: number | null,
    serviceType: string | null,
    chargePrice: number | null,
    shipmentPrices?: number[] | null,
    carrier?: string | null,
    carrierServiceCode?: string | null,
    carrierOptions?: Record<string, unknown>[] | null,
  ): Promise<CheckoutData> {
    const endpoint = `/api/v2${guestPrefix()}/cart/shipping-rate`;
    const response = await fetchWithErrorHandling<{ data: CheckoutData }>(endpoint, {
      method: 'POST',
      body: JSON.stringify({
        shippingLocationId: locationId,
        shippingServiceType: serviceType,
        shippingChargePrice: chargePrice,
        shippingShipmentPrices: shipmentPrices ?? null,
        shippingCarrier: carrier ?? null,
        shippingCarrierServiceCode: carrierServiceCode ?? null,
        shippingCarrierOptions: carrierOptions ?? null,
      }),
    });
    return response.data;
  }

  static async applyPromo(
    promoId: number | null,
    shippingChargePrice: number | null = null,
    shippingLocationId: number | null = null,
    shippingServiceType: string | null = null,
  ): Promise<CheckoutData> {
    const endpoint = `/api/v2${guestPrefix()}/cart/promo`;
    const response = await fetchWithErrorHandling<{ data: CheckoutData }>(endpoint, {
      method: 'POST',
      body: JSON.stringify({ promoId, shippingChargePrice, shippingLocationId, shippingServiceType }),
    });
    return response.data;
  }

  static async getCheckoutData(): Promise<CheckoutData> {
    try {
      // Try API first - use guest or authenticated endpoint based on auth state
      try {
        const endpoint = `/api/v2${guestPrefix()}/checkout`;
        const response = await fetchWithErrorHandling<{ data: CheckoutData }>(endpoint);
        return response.data;
      } catch (apiError) {
        console.warn('API not available, falling back to local data:', apiError);

        // Fall back to local JSON file
        try {
          const { default: localCheckoutData } = await import('../data/checkout.sample.data.json');
          return localCheckoutData as CheckoutData;
        /* c8 ignore start */
        } catch (localError) {
          console.error('Failed to load local checkout data:', localError);
          throw new Error('Unable to load checkout data from both API and local data.');
        }
        /* c8 ignore stop */
      }
    /* c8 ignore start */
    } catch (error) {
      console.error('Failed to fetch checkout data:', error);
      throw new Error('Unable to load checkout data. Please try again later.');
    }
    /* c8 ignore stop */
  }

  static async getDiagnosticData(): Promise<CheckoutDiagnosticData> {
    const response = await fetchWithErrorHandling<{ data: CheckoutDiagnosticData }>('/api/v2/checkout/diagnostic');
    return response.data;
  }
}
